/**
 * @method getMobilePseudoIDBObjects
 *
 * Creates a IDB-like proxy to React Native that mirrors the structure in getIDBObjects.js
 *
 * @param {Number} bookPk - a Book primary key
 * @param {String} storeName - the name of the store to access
 * @returns {Object} object containing a db instance, a transaction instance, and a store
 */
const getMobilePseudoIDBObjects = async (bookPk, storeName) => {
  if (!bookPk) {
    throw new Error('A bookPk is required');
  }

  const transaction = { complete: Promise.resolve() };

  const postMessage = function (data) {
    window.RedshelfWebView?.postMessage(data);
  };

  const store = {
    openCursor: () => {
      return new Promise((resolve) => {
        // Set up a listener to receive data from React Native
        const handleMessage = (event) => {
          // Ignore irrelevant messages.
          if (
            event.data.type !== 'OFFLINE_USER_CONTENT_ITEMS' ||
            event.data.bookPk !== bookPk ||
            event.data.storeName !== storeName
          ) {
            return;
          }

          window.removeEventListener('message', handleMessage);

          const items = event.data.userContentItems;
          const keys = Object.keys(items);

          let count = 0;
          const cursorBuilder = (count) => {
            const key = keys[count];
            const value = items[key];

            return {
              key,
              value,
              continue: () => {
                count += 1;

                if (count < keys.length) {
                  return cursorBuilder(count);
                }

                return null;
              },
            };
          };

          resolve(keys.length ? cursorBuilder(count) : null);
        };

        window.addEventListener('message', handleMessage);

        // Send get command to React Native
        postMessage(
          JSON.stringify({
            type: 'OFFLINE_USER_CONTENT_GET_ITEMS',
            data: {
              bookPk,
              storeName,
            },
          }),
        );
      });
    },
    put: (value, key) => {
      return new Promise((resolve) => {
        // Set up a listener to receive an acknowledgment from React Native
        const handleMessage = (event) => {
          // Ignore irrelevant messages.
          if (
            event.data.type !== 'OFFLINE_USER_CONTENT_PUT_ACK' ||
            event.data.bookPk !== bookPk ||
            event.data.storeName !== storeName ||
            event.data.key !== key
          ) {
            return;
          }

          window.removeEventListener('message', handleMessage);

          // A successful acknowledgement will resolve the promise
          resolve();
        };

        window.addEventListener('message', handleMessage);

        // Send put command to React Native
        postMessage(
          JSON.stringify({
            type: 'OFFLINE_USER_CONTENT_PUT',
            data: {
              bookPk,
              storeName,
              key,
              value,
            },
          }),
        );
      });
    },
    delete: (key) => {
      return new Promise((resolve) => {
        // Set up a listener to receive an acknowledgment from React Native
        const handleMessage = (event) => {
          // Ignore irrelevant messages.
          if (
            event.data.type !== 'OFFLINE_USER_CONTENT_DELETE_ACK' ||
            event.data.bookPk !== bookPk ||
            event.data.storeName !== storeName ||
            event.data.key !== key
          ) {
            return;
          }

          window.removeEventListener('message', handleMessage);

          // A successful acknowledgement will resolve the promise
          resolve();
        };

        window.addEventListener('message', handleMessage);

        // Send delete command to React Native
        postMessage(
          JSON.stringify({
            type: 'OFFLINE_USER_CONTENT_DELETE',
            data: {
              bookPk,
              storeName,
              key,
            },
          }),
        );
      });
    },
  };

  return { db: {}, transaction, store };
};

export default getMobilePseudoIDBObjects;
